/**
 * Defensive patch for Node.prototype.removeChild / insertBefore (the classic
 * facebook/react#11538 mitigation). Google-Translate-class browser features
 * rewrite text nodes under React's feet; React's next commit then calls
 * removeChild/insertBefore with a child whose real parent has changed and the
 * DOM throws NotFoundError, crashing the whole tree. This guard degrades those
 * calls instead of throwing, and only those calls — every valid operation
 * delegates to the untouched originals.
 */

export type DomGuardOp = "removeChild" | "insertBefore";

/** Called at most once per op per page load, on the first intercepted call. */
export type DomGuardReporter = (op: DomGuardOp) => void;

let installed = false;
const reportedOps = new Set<DomGuardOp>();

export function installDomMutationGuard(report?: DomGuardReporter): () => void {
  if (typeof Node === "undefined" || installed) {
    return () => undefined;
  }
  installed = true;

  const originalRemoveChild = Node.prototype.removeChild;
  const originalInsertBefore = Node.prototype.insertBefore;

  const reportOnce = (op: DomGuardOp) => {
    if (reportedOps.has(op)) return;
    reportedOps.add(op);
    console.warn(
      `domMutationGuard: intercepted invalid ${op} (external DOM mutation, e.g. page translation)`
    );
    try {
      report?.(op);
    } catch {
      // A throwing reporter must never break the DOM operation itself.
    }
  };

  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      reportOnce("removeChild");
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null
  ): T {
    // A null reference is the legal append case — never intercept it.
    if (referenceNode != null && referenceNode.parentNode !== this) {
      reportOnce("insertBefore");
      return originalInsertBefore.call(this, newNode, null) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };

  return () => {
    Node.prototype.removeChild = originalRemoveChild;
    Node.prototype.insertBefore = originalInsertBefore;
    installed = false;
    reportedOps.clear();
  };
}
